import SoftBadge from 'components/common/SoftBadge';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from 'components/common/IconButton';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import AdvanceTableFooter from './AdvanceTableFooter';
import AdvanceTablePagination from './AdvanceTablePagination';
import AdvanceTableSearchBox from './AdvanceTableSearchBox';
import { Can } from 'routes/permissions/Can';
import { userActions } from 'routes/permissions/permissionKeys';

export const BULK_ACTION_EDIT = 'EDIT';
export const BULK_ACTION_DELETE = 'DELETE';

// NOTE: defined at module scope (not inside CustomAdvanceTable) on purpose.
// When this was an inner function, every parent re-render produced a new
// component type, so React unmounted/remounted the whole header — including
// the search input, which lost focus mid-typing whenever server-side results
// arrived.
/* eslint-disable react/prop-types */
function BulAction({
  selectedRowIds,
  globalFilter,
  setGlobalFilter,
  title,
  showSearchInput,
  searchInputPlaceHolder,
  bulkActionsOptions,
  handleBulkAction,
  headerContent,
  addNew,
  subject,
  onClickAddNew,
  addButtonIcon,
  addButtonLabel,
  hasFilters,
  handleFilter,
  hasExport,
  handleExport,
  filteredRowCount
}) {
  const objectLength = Object.keys(selectedRowIds).length;
  const [selectBulkAction, setSelectBulkAction] = useState();
  const selectedIds = Object.keys(selectedRowIds).map(id => id);
  return (
    <Row className="flex-between-center mb-3 ">
      <Col
        sm="auto"
        className="d-md-flex align-items-center justify-content-md-start pe-md-0"
      >
        <h5
          className="fs-0 mb-0 text-nowrap py-2 py-xl-0 fw-medium"
          style={{ fontSize: '1.25rem' }}
        >
          {objectLength > 0
            ? 'You have selected ' +
              objectLength +
              ` row${objectLength > 1 ? 's' : ''}`
            : title}
          {/* Total after search/filters - the number staff asked to always see */}
          {objectLength === 0 && typeof filteredRowCount === 'number' && (
            <SoftBadge pill bg="secondary" className="ms-2 fs--1 align-middle">
              {filteredRowCount}
            </SoftBadge>
          )}
        </h5>

        {showSearchInput && (
          <div className="ms-md-4 mt-2 mt-md-0">
            <AdvanceTableSearchBox
              className="table-input-search-width"
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              placeholder={searchInputPlaceHolder}
              showSearchIcon={false}
            />
          </div>
        )}
      </Col>
      <Col sm="auto" className="text-md-end ps-md-0 mt-3 mt-md-0">
        {Object.keys(selectedRowIds).length > 0 ? (
          <div className="d-flex">
            <Form.Select
              onChange={e => setSelectBulkAction(e.target.value)}
              value={selectBulkAction}
              size="sm"
              aria-label="Bulk actions"
              name="bulkActions"
            >
              <option>Bulk Actions</option>
              {bulkActionsOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Button
              type="button"
              variant="falcon-default"
              size="sm"
              className="ms-2"
              onClick={() => handleBulkAction(selectBulkAction, selectedIds)}
            >
              Apply
            </Button>
          </div>
        ) : (
          <div id="orders-actions">
            {!!headerContent && headerContent}
            {addNew && (
              <Can I={userActions.create} a={subject}>
                <IconButton
                  variant="primary"
                  size="sm"
                  icon={addButtonIcon}
                  iconClassName="text-light"
                  transform="shrink-3"
                  onClick={onClickAddNew}
                  className="me-2"
                  style={{ fontSize: '1rem' }}
                >
                  <span className="text-light d-none d-lg-inline-block ms-2">
                    {addButtonLabel}
                  </span>
                </IconButton>
              </Can>
            )}
            {hasFilters && (
              <IconButton
                variant="falcon-default"
                size="sm"
                icon="filter"
                transform="shrink-3"
                className="me-2"
                onClick={() => handleFilter()}
                style={{ fontSize: '1rem' }}
              >
                <span className="d-none d-lg-inline-block ms-2">Filter</span>
              </IconButton>
            )}
            {hasExport && (
              <IconButton
                variant="falcon-default"
                size="sm"
                icon="external-link-alt"
                transform="shrink-3"
                onClick={handleExport}
                style={{ fontSize: '1rem' }}
              >
                <span className="d-none d-lg-inline-block ms-2">Export</span>
              </IconButton>
            )}
          </div>
        )}
      </Col>
    </Row>
  );
}
/* eslint-enable react/prop-types */

const CustomAdvanceTable = ({
  columns,
  data,
  title = 'Selection Example',
  addButtonLabel = 'New',
  addButtonIcon = 'plus',
  showSearchInput = false,
  searchInputPlaceHolder = 'Search by name',
  onClickAddNew = () => {},
  addNew = false,
  rowsPerPageSelection = false,
  viewAllBtn = true,
  perPage = 50,
  showTablePagination = true,
  loading,
  bulkActionsOptions,
  handleBulkAction,
  selection = false,
  hasFilters,
  hasExport,
  handleExport,
  headerContent,
  subject,
  handleFilter,
  pagination = {
    showInfo: true,
    showNavButtons: true
  },
  // Opt-in server-side pagination/search (see AdvanceTableWrapper). Omit for
  // the default client-side behaviour.
  manualPagination = false,
  controlledPageCount,
  onServerStateChange,
  // Server mode only: start on this page / with this search term (used to
  // restore where the user was when they come back to the list)
  initialPageIndex,
  initialGlobalFilter
}) => {
  return (
    <Card className="border shadow-none">
      <Card.Body>
        <AdvanceTableWrapper
          columns={columns}
          data={data}
          sortable
          pagination
          perPage={perPage}
          selection={selection}
          selectionColumnWidth={30}
          manualPagination={manualPagination}
          controlledPageCount={controlledPageCount}
          onServerStateChange={onServerStateChange}
          initialPageIndex={initialPageIndex}
          initialGlobalFilter={initialGlobalFilter}
        >
          <BulAction
            table
            title={title}
            showSearchInput={showSearchInput}
            searchInputPlaceHolder={searchInputPlaceHolder}
            bulkActionsOptions={bulkActionsOptions}
            handleBulkAction={handleBulkAction}
            headerContent={headerContent}
            addNew={addNew}
            subject={subject}
            onClickAddNew={onClickAddNew}
            addButtonIcon={addButtonIcon}
            addButtonLabel={addButtonLabel}
            hasFilters={hasFilters}
            handleFilter={handleFilter}
            hasExport={hasExport}
            handleExport={handleExport}
          />
          <AdvanceTable
            table
            headerClassName="bg-200 text-900 text-nowrap align-middle"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              striped: true,
              className: 'fs--1 mb-0 overflow-hidden',
              size: 'sm'
            }}
            loading={loading}
          />
          <div className="mt-3">
            {showTablePagination ? (
              <AdvanceTablePagination table />
            ) : (
              <AdvanceTableFooter
                rowCount={data.length}
                table
                rowInfo={pagination.showInfo}
                navButtons={pagination.showNavButtons}
                onClickViewAll={pagination.onClickViewAll}
                rowsPerPageSelection={rowsPerPageSelection}
                viewAllBtn={viewAllBtn}
                perPage={perPage}
              />
            )}
          </div>
        </AdvanceTableWrapper>
      </Card.Body>
    </Card>
  );
};

CustomAdvanceTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  title: PropTypes.string,
  addButtonLabel: PropTypes.string,
  addButtonIcon: PropTypes.string,
  searchInputPlaceHolder: PropTypes.string,
  showSearchInput: PropTypes.bool,
  onClickAddNew: PropTypes.func,
  rowClickEvent: PropTypes.func,
  addNew: PropTypes.bool,
  content: PropTypes.node,
  rowsPerPageSelection: PropTypes.bool,
  viewAllBtn: PropTypes.bool,
  showTablePagination: PropTypes.bool,
  perPage: PropTypes.number,
  loading: PropTypes.bool,
  bulkActionsOptions: PropTypes.array,
  handleBulkAction: PropTypes.func,
  selection: PropTypes.bool,
  hasFilters: PropTypes.bool,
  hasExport: PropTypes.bool,
  handleExport: PropTypes.func,
  headerContent: PropTypes.node,
  subject: PropTypes.string,
  handleFilter: PropTypes.func,
  pagination: PropTypes.shape({
    showInfo: PropTypes.bool,
    showNavButtons: PropTypes.bool,
    onClickViewAll: PropTypes.func
  }),
  manualPagination: PropTypes.bool,
  initialPageIndex: PropTypes.number,
  initialGlobalFilter: PropTypes.string,
  controlledPageCount: PropTypes.number,
  onServerStateChange: PropTypes.func
};

export default CustomAdvanceTable;
